import { api } from "./api/client.js";
import { clearViewingAs, setViewingAs } from "./viewAsStore.js";

// Starting and ending "view as" in the dashboard. The admin panel opens `/?viewAs=<userId>` in a new
// tab; once the Super admin is signed in here, the start is recorded (and refused for anyone else)
// with POST /admin/access/view-as, the user is kept in this tab's sessionStorage and the dashboard
// reloads. Both directions reload, so the clients list, permissions and every screen are rebuilt
// from one consistent set of rights rather than patched.

export const VIEW_AS_PARAM = "viewAs";

const requestedUserId = () => new URLSearchParams(window.location.search).get(VIEW_AS_PARAM);

// `requested` until the start has been tried, `starting` while it is (the boot waits for both:
// whatever it loaded would be thrown away by the reload), then `done`. A refused start leaves its
// message in `error` and the dashboard boots as the signed-in user.
export const viewAsEntry = $state({
  status: requestedUserId() ? "requested" : "done",
  error: null,
});

// Keeps the request through the Auth0 round trip: the login guard returns to the router's path,
// which carries no query string.
export function withViewAsRequest(path) {
  const userId = requestedUserId();
  return userId ? `${path}?${VIEW_AS_PARAM}=${encodeURIComponent(userId)}` : path;
}

function forgetViewAsRequest() {
  const url = new URL(window.location.href);
  url.searchParams.delete(VIEW_AS_PARAM);
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
}

export async function startViewingAsFromUrl() {
  const userId = requestedUserId();
  if (viewAsEntry.status !== "requested" || !userId) return;
  viewAsEntry.status = "starting";
  // A view already running in this tab must not ride along on the request that starts the next one.
  clearViewingAs();
  try {
    const viewing = await api.post("/admin/access/view-as", { userId, app: "dashboard" });
    setViewingAs({
      userId: viewing?.userId ?? userId,
      email: viewing?.email ?? "",
      canWrite: viewing?.canWrite === true,
    });
  } catch (error) {
    forgetViewAsRequest();
    viewAsEntry.error = error?.message || String(error);
    viewAsEntry.status = "done";
    return;
  }
  forgetViewAsRequest();
  window.location.reload();
}

export function stopViewingAs() {
  clearViewingAs();
  window.location.assign("/");
}
